import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { ResetState } from '../../../shared/state/meta.actions';
import { catchError, EMPTY, tap } from 'rxjs';
import { LoadableStatus } from '@aiwyn/shared';
import { CxpDeliverableDetailService } from '../services/cxp-deliverable-detail.service';
import { CxpDeliverableApiService, MarkDeliverableFilesActions } from '../services/cxp-deliverable-api.service';
import {
  ClearCxpDeliverableDetail,
  HydrateCxpDeliverableDetailFromTasks,
  LoadCxpDeliverableDetail,
  LoadCxpDeliverableDetailFailure,
  LoadCxpDeliverableDetailSuccess,
  MarkCxpDeliverableViewed,
} from './cxp-deliverable-detail.actions';
import { CxpDeliverableDetailStateModel, DEFAULT_CXP_DELIVERABLE_DETAIL_STATE } from './cxp-deliverable-detail.model';
import { SetCxpActiveRouteParams } from './cxp-tasks.actions';
import { CxpDeliverableDetail } from '../models';

const EMPTY_DETAIL_BASE: Omit<CxpDeliverableDetail, 'id'> = {
  name: '',
  description: '',
  serviceLineName: null,
  clientName: null,
  files: [],
  clientContacts: [],
  teamMembers: [],
};

export const CXP_DELIVERABLE_DETAIL_STATE_NAME = 'cxpDeliverableDetail';

@State<CxpDeliverableDetailStateModel>({
  name: CXP_DELIVERABLE_DETAIL_STATE_NAME,
  defaults: DEFAULT_CXP_DELIVERABLE_DETAIL_STATE,
})
@Injectable()
export class CxpDeliverableDetailState {
  private readonly detailService = inject(CxpDeliverableDetailService);
  private readonly deliverableApi = inject(CxpDeliverableApiService);

  @Action(ResetState)
  resetState({ setState }: StateContext<CxpDeliverableDetailStateModel>): void {
    setState(DEFAULT_CXP_DELIVERABLE_DETAIL_STATE);
  }

  @Action(SetCxpActiveRouteParams)
  setActiveRouteParams(ctx: StateContext<CxpDeliverableDetailStateModel>, { deliverableId }: SetCxpActiveRouteParams) {
    ctx.patchState({ selectedDeliverableId: deliverableId });

    if (deliverableId === null) {
      ctx.dispatch(new ClearCxpDeliverableDetail());
    }
  }

  @Action(HydrateCxpDeliverableDetailFromTasks)
  hydrateFromTasks(ctx: StateContext<CxpDeliverableDetailStateModel>, { partial }: HydrateCxpDeliverableDetailFromTasks) {
    const state = ctx.getState();
    const existing = state.detail;

    // If we already have the fully-loaded detail for this id, hydration is a
    // no-op — we don't want to overwrite real data with the slim task-list shape.
    if (existing?.id === partial.id && state.status === LoadableStatus.LOADED) {
      return;
    }

    // Start from the existing detail when ids match (preserves partial fields
    // already merged), otherwise start from the empty base (fresh hydration
    // for a different deliverable).
    const base: CxpDeliverableDetail = existing?.id === partial.id ? existing : { ...EMPTY_DETAIL_BASE, id: partial.id };

    ctx.patchState({ detail: { ...base, ...partial } });
  }

  @Action(LoadCxpDeliverableDetail)
  loadDetail(ctx: StateContext<CxpDeliverableDetailStateModel>, { deliverableId }: LoadCxpDeliverableDetail) {
    const state = ctx.getState();

    // Clear stale data only when switching to a different deliverable; when
    // re-loading the same id (e.g. refresh) we keep whatever's there so the
    // header keeps rendering through the loading state.
    const detailToKeep = state.detail?.id === deliverableId ? state.detail : null;

    ctx.patchState({ status: LoadableStatus.LOADING, detail: detailToKeep });

    return this.detailService.fetch(deliverableId).pipe(
      tap((detail) => ctx.dispatch(new LoadCxpDeliverableDetailSuccess(detail))),
      catchError((error) => {
        ctx.dispatch(new LoadCxpDeliverableDetailFailure(error));
        return EMPTY;
      }),
    );
  }

  @Action(LoadCxpDeliverableDetailSuccess)
  loadDetailSuccess(ctx: StateContext<CxpDeliverableDetailStateModel>, { detail }: LoadCxpDeliverableDetailSuccess) {
    ctx.patchState({ status: LoadableStatus.LOADED, detail });

    // We mark all as viewed - Decided by product
    const fileIds = detail.files.map((file) => file.id);
    ctx.dispatch(new MarkCxpDeliverableViewed(detail.id, fileIds));
  }

  @Action(LoadCxpDeliverableDetailFailure)
  loadDetailFailure(ctx: StateContext<CxpDeliverableDetailStateModel>) {
    ctx.patchState({ status: LoadableStatus.ERROR });
  }

  @Action(ClearCxpDeliverableDetail)
  clearDetail(ctx: StateContext<CxpDeliverableDetailStateModel>) {
    ctx.patchState({ status: LoadableStatus.DEFAULT, detail: null });
  }

  @Action(MarkCxpDeliverableViewed)
  markAsViewed(_ctx: StateContext<CxpDeliverableDetailStateModel>, { deliverableId, fileIds }: MarkCxpDeliverableViewed) {
    return this.deliverableApi
      .markDeliverableFiles(deliverableId, fileIds, MarkDeliverableFilesActions.VIEWED)
      .pipe(catchError(() => EMPTY));
  }
}
